import { datadogRum } from "@datadog/browser-rum";
import { globalCopilotIds } from "@zapier/copilot-ui";
import type { CopilotClientLogger } from "@zapier/copilot-ui/client";

import { COPILOT_SOURCE } from "@/app/app/get-help/constants";
import { logError } from "../utils/logError";

/**
 * Builds a {@link CopilotClientLogger} for a Copilot surface, fanning failures
 * out to both sinks: Sentry (via `logError`, primary) and Datadog RUM
 * (best-effort — `addError` is a no-op outside production where RUM isn't
 * initialized).
 *
 * Three filterable dimensions are recorded:
 * - **surface** — the canonical Copilot Platform surface id
 *   (`globalCopilotIds.support`, the single source of truth shared across
 *   surfaces; matches the `copilotId` these providers run). Used as the Sentry
 *   `feature` tag so cross-surface filtering stays consistent with other
 *   Copilot consumers (editor, memberhome, …).
 * - **source** — which UI within this surface reported the failure
 *   (`get-help` full page vs. `chat-widget` iframe), from `COPILOT_SOURCE`.
 * - **failure type** — the library's own `feature` value (e.g.
 *   `support-chat-csat`, `support-chat-poll`), carried through in `context`.
 */
export function createCopilotClientLogger(source: string): CopilotClientLogger {
  const surface = globalCopilotIds.support;
  return {
    error(error, context) {
      logError(error.message, error, {
        app: "support-chat",
        feature: surface,
        extra: { surface, source, ...context },
      });
      datadogRum.addError(error, { surface, source, ...context });
    },
    warn(message, context) {
      logError(message, undefined, {
        app: "support-chat",
        feature: surface,
        extra: { surface, source, ...context },
      });
      datadogRum.addError(new Error(message), { surface, source, ...context });
    },
  };
}

export const getHelpCopilotLogger = createCopilotClientLogger(
  COPILOT_SOURCE.fullscreen,
);

export const supportCopilotWidgetLogger = createCopilotClientLogger(
  COPILOT_SOURCE.widget,
);
